import { useRoute } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  AlertTriangle,
  Eye,
  Lock,
  ExternalLink,
  FileDown,
  FileText,
  Loader2,
} from "lucide-react";
import ReportVisualization from "@/components/reports/ReportVisualization";
import { useState } from "react";

export default function PublicReport() {
  const [, params] = useRoute("/public/report/:token");
  const token = params?.token;
  const { toast } = useToast();

  const { data: reportData, isLoading, error } = useQuery({
    queryKey: ["public-report", token],
    queryFn: async () => {
      const response = await fetch(`/api/public/reports/${token}`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to load report");
      }
      return response.json();
    },
    enabled: !!token,
    retry: false,
  });

  // Fetch full dashboard data for visualization
  const { data: dashboardData, isLoading: isDashboardLoading, error: dashboardError } = useQuery({
    queryKey: ["public-dashboard", reportData?.assessmentId],
    queryFn: async () => {
      if (!reportData?.assessmentId) return null;
      const response = await fetch(`/api/public/reports/${token}/dashboard`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to load dashboard data");
      }
      return response.json();
    },
    enabled: !!reportData?.assessmentId,
    retry: false,
  });

  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  
  const handleGeneratePdf = async () => {
    if (!dashboardData) {
      toast({
        title: "Error",
        description: "Report data not available",
        variant: "destructive",
      });
      return;
    }
    
    setIsGeneratingPdf(true);
    try {
      const response = await fetch(`/api/public/reports/${token}/pdf`);

      if (!response.ok) {
        throw new Error("Failed to generate PDF");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Sustainability_Report_${reportData.organizationName.replace(/[^a-zA-Z0-9]/g, "_")}_FY${reportData.reportingYear}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "PDF Downloaded",
        description: "Your sustainability report PDF has been downloaded",
      });
    } catch (error) {
      console.log("PDF generation error:", error);
      toast({
        title: "Error",
        description: "Failed to generate PDF",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  if (isLoading || isDashboardLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-96 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
        <div className="max-w-3xl mx-auto mt-20">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{"Report Not Available"}</AlertTitle>
            <AlertDescription>
              {error instanceof Error ? error.message : "This link has expired or is no longer valid"}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // Check if dashboard has error (e.g., assessment not locked)
  if (dashboardError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
        <div className="max-w-3xl mx-auto mt-20">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{"Report Not Available"}</AlertTitle>
            <AlertDescription>
              {"This link is not working properly"}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return null;
  }

  // Don't show data if there's a dashboard error (e.g., not locked)
  if (dashboardError || !dashboardData) {
    // Errors are already handled above, this shouldn't render
    return null;
  }

  const { organizationName, reportingYear, reportingPeriod, viewCount } = reportData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-heading font-bold" style={{ color: '#009687' }}>
                {organizationName}
              </h1>
              <p className="text-lg text-muted-foreground mt-1">
                {"Sustainability Report"} • {reportingPeriod || reportingYear}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                <Eye className="h-3 w-3" />
                {`${viewCount} views`}
              </Badge>
              
              <Button 
                className="gap-2"
                style={{ backgroundColor: '#00444C' }}
                onClick={handleGeneratePdf}
                disabled={isGeneratingPdf}
              >
                {isGeneratingPdf ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileDown className="h-4 w-4" />
                )}
                {isGeneratingPdf ? "Generating..." : "Download PDF"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-8 py-8 space-y-8">
        {/* Public Access Notice */}
        <Alert className="bg-blue-50 border-blue-200">
          <Lock className="h-4 w-4 text-blue-600" />
          <AlertTitle className="text-blue-800">{"Public Report Access"}</AlertTitle>
          <AlertDescription className="text-blue-800/80">
            {`This is a read-only public view of a sustainability report shared by ${organizationName}.`}
          </AlertDescription>
        </Alert>

        {/* Report Visualization Component */}
        {dashboardData && <ReportVisualization data={dashboardData} showStatusAlert={false} />}

        {/* CTA */}
        <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
          <CardContent className="pt-6 text-center space-y-4">
            <div>
              <h3 className="text-lg font-semibold" style={{ color: '#00444C' }}>
                {"Want to measure your own sustainability baseline?"}
              </h3>
              <p className="text-sm text-muted-foreground mt-2">
                {"180Pi helps African SMEs measure, report, and improve their sustainability performance"}
              </p>
            </div>
            <Button 
              size="lg"
              className="gap-2"
              style={{ backgroundColor: '#00444C' }}
              onClick={() => window.location.href = '/'}
            >
              <ExternalLink className="h-4 w-4" />
              {"Visit 180Pi"}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <div className="max-w-7xl mx-auto px-8 py-6 text-center text-sm text-muted-foreground border-t mt-12">
        <p>{"Powered by 180Pi • VSME-Compliant Sustainability Reporting for African SMEs"}</p>
      </div>
    </div>
  );
}
